import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Mail, Phone, Building2, Linkedin, MoreHorizontal, Eye, Trash, Pencil, Heart, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
  { value: 'estrategico', label: 'Estratégico — 15 días' },
  { value: 'prioritario', label: 'Prioritario — 30 días' },
  { value: 'activo', label: 'Activo — 60 días' },
  { value: 'orbita', label: 'En órbita — 90 días' },
  { value: 'no_seguir', label: 'No seguir' },
];

// Contact Card Component - reusable across the app
const ContactCard = ({
  contact,
  companies = [],
  typsaOrgUnits = [],
  isEditor = false,
  onEdit,
  onDelete,
  onUpdate,
  showCompanyName = true,
}) => {
  const navigate = useNavigate();
  const { editorUsers, currentUser } = useUser();
  const [frequency, setFrequency] = useState(contact.cultivation_frequency || '__none__');
  const [owner, setOwner] = useState(contact.owner || '__none__');

  useEffect(() => {
    setFrequency(contact.cultivation_frequency || '__none__');
    setOwner(contact.owner || '__none__');
  }, [contact.cultivation_frequency, contact.owner]);

  const patchField = async (field, value) => {
    try {
      const payload = { [field]: value === '__none__' ? null : value };
      const response = await axios.patch(`${API}/contacts/${contact.id}`, payload, {
        headers: { 'X-User-Email': currentUser?.email },
      });
      toast.success(field === 'cultivation_frequency' ? 'Frecuencia actualizada' : 'Responsable actualizado');
      onUpdate?.(response.data);
    } catch (err) {
      console.error(err);
      toast.error('Error al actualizar');
      // revert local state on error
      if (field === 'cultivation_frequency') setFrequency(contact.cultivation_frequency || '__none__');
      if (field === 'owner') setOwner(contact.owner || '__none__');
    }
  };

  const handleFrequencyChange = (val) => {
    setFrequency(val);
    patchField('cultivation_frequency', val);
  };

  const handleOwnerChange = (val) => {
    setOwner(val);
    patchField('owner', val);
  };

  // Get full name
  const getFullName = () => {
    const firstName = contact.first_name || contact.name || '';
    const lastName = contact.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'Sin nombre';
  };

  // Get company name
  const getCompanyName = () => {
    if (contact.company_id) {
      const company = companies.find((c) => c.id === contact.company_id);
      return company?.name || 'Empresa desconocida';
    }
    if (contact.typsa_org_unit_id) {
      const unit = typsaOrgUnits.find((u) => u.id === contact.typsa_org_unit_id);
      return unit?.value || 'TYPSA';
    }
    return 'Sin empresa';
  };

  // Get company type info for badges
  const getCompanyInfo = () => {
    if (contact.typsa_org_unit_id) {
      const orgUnit = typsaOrgUnits.find((u) => u.id === contact.typsa_org_unit_id);
      if (orgUnit) {
        return {
          type: 'typsa',
          typeLabel: 'TYPSA',
          typeColor: 'bg-blue-100 text-blue-800 border-blue-300',
          category: orgUnit.category,
          categoryLabel:
            orgUnit.category === 'division'
              ? 'División'
              : orgUnit.category === 'departamento'
              ? 'Departamento'
              : orgUnit.category === 'territorio'
              ? 'Territorio'
              : orgUnit.category === 'grupo'
              ? 'Grupo'
              : null,
        };
      }
    }
    const company = companies.find((c) => c.id === contact.company_id);
    if (company) {
      if (company.company_type === 'typsa' || company.name?.toUpperCase().includes('TYPSA')) {
        return { type: 'typsa', typeLabel: 'TYPSA', typeColor: 'bg-red-100 text-red-800 border-red-300', category: null, categoryLabel: null };
      } else if (company.company_type === 'publica') {
        return { type: 'publica', typeLabel: 'Pública', typeColor: 'bg-green-100 text-green-800 border-green-300', category: null, categoryLabel: null };
      } else {
        return { type: 'privada', typeLabel: 'Privada', typeColor: 'bg-purple-100 text-purple-800 border-purple-300', category: null, categoryLabel: null };
      }
    }
    return null;
  };

  const companyInfo = getCompanyInfo();
  const handleCardClick = () => navigate(`/contacts/${contact.id}`);

  return (
    <Card
      data-testid={`contact-card-${contact.id}`}
      className="relative cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleCardClick}
    >
      <CardContent className="p-4">
        {/* Three-dot menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7" data-testid={`contact-menu-${contact.id}`}>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${contact.id}`); }} data-testid={`view-contact-${contact.id}`}>
              <Eye className="w-4 h-4 mr-2" />
              Ver
            </DropdownMenuItem>
            {isEditor && onEdit && (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(contact); }} data-testid={`edit-contact-${contact.id}`}>
                <Pencil className="w-4 h-4 mr-2" />
                Editar
              </DropdownMenuItem>
            )}
            {isEditor && onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onDelete(contact); }}
                  className="text-destructive focus:text-destructive"
                  data-testid={`delete-contact-${contact.id}`}
                >
                  <Trash className="w-4 h-4 mr-2" />
                  Eliminar
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex items-start gap-3 pr-8">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{getFullName()}</div>
            {contact.role && (
              <div className="text-xs text-muted-foreground truncate">{contact.role}</div>
            )}
            {companyInfo && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${companyInfo.typeColor}`}>
                  {companyInfo.typeLabel}
                </span>
                {companyInfo.categoryLabel && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-slate-100 text-slate-700 border-slate-300">
                    {companyInfo.categoryLabel}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 space-y-1.5 overflow-hidden">
          {contact.email && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
              <Mail className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate block">{contact.email}</span>
            </div>
          )}
          {contact.phone && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
              <Phone className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate block">{contact.phone}</span>
            </div>
          )}
          {showCompanyName && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
              <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate block">{getCompanyName()}</span>
            </div>
          )}
          {contact.linkedin && (
            <a
              href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 min-w-0"
              onClick={(e) => e.stopPropagation()}
            >
              <Linkedin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate block">LinkedIn</span>
            </a>
          )}
          {/* Days since last contact/activity */}
          <div className="flex items-center gap-2 text-xs min-w-0" data-testid={`last-contact-${contact.id}`}>
            <Clock className="w-3.5 h-3.5 flex-shrink-0 text-slate-500" />
            <span className={`truncate ${
              contact.days_since_last_contact == null
                ? 'text-muted-foreground'
                : contact.days_since_last_contact >= 60
                ? 'text-red-600 font-medium'
                : contact.days_since_last_contact >= 30
                ? 'text-orange-600'
                : 'text-muted-foreground'
            }`}>
              {contact.days_since_last_contact == null
                ? 'Sin contacto registrado'
                : contact.days_since_last_contact === 0
                ? 'Contactado hoy'
                : `Hace ${contact.days_since_last_contact} día${contact.days_since_last_contact === 1 ? '' : 's'}`}
            </span>
          </div>
        </div>

        {/* Quick-edit dropdowns: cultivation frequency + owner */}
        {isEditor && (
          <div
            className="mt-3 pt-3 border-t space-y-2"
            onClick={(e) => e.stopPropagation()}
            data-testid={`contact-quick-edit-${contact.id}`}
          >
            <div className="flex items-center gap-2">
              <Heart className="w-3.5 h-3.5 text-pink-500 flex-shrink-0" />
              <Select value={frequency} onValueChange={handleFrequencyChange}>
                <SelectTrigger className="h-8 text-xs" data-testid={`contact-frequency-select-${contact.id}`}>
                  <SelectValue placeholder="Sin configurar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin configurar</SelectItem>
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
              <Select value={owner} onValueChange={handleOwnerChange}>
                <SelectTrigger className="h-8 text-xs" data-testid={`contact-owner-select-${contact.id}`}>
                  <SelectValue placeholder="Sin responsable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin responsable</SelectItem>
                  {(editorUsers || []).map((u) => (
                    <SelectItem key={u.email} value={u.name}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ContactCard;
